import React, { createContext, useContext } from 'react';
import { 
  MdOutlinePeopleAlt,
  MdOutlineBusiness,
  MdOutlineAccessTime,
  MdOutlineEventNote,
  MdOutlinePieChartOutline
} from 'react-icons/md';
import { FaRupeeSign } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Cookies from "universal-cookie";
import useGet from "../hooks/read/useGet";
import useHasProductAccess from '../hooks/useHasProductAccess';
import { ROLE, ROLES } from '../config';

import useUpdateCompanyCompletionStatus from '../hooks/onboarding/useUpdateCompanyCompletionStatus';

const cookies = new Cookies();

const OnboardingContext = createContext(null);

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboardingContext must be used within an OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const navigate = useNavigate();
  const { hasAccess, companyCompletionStatus, isCheckingAccess } = useHasProductAccess();
  const { 
    confirmModalData, 
    promptComplete, 
    promptIncomplete, 
    confirmStatusChange, 
    closeConfirmationModal, 
    isLoading: isUpdatingStatus 
  } = useUpdateCompanyCompletionStatus();
  const role = cookies.get(ROLE);
  const isAdmin = role === ROLES.COMPANY_ADMIN;

  const { list } = useGet({
    url: "dashboard/company_completion_status",
    enabled: isAdmin,
  });

  const apiData = list?.data?.data || {};

  const cards = [
    {
      id: 1,
      slug: apiData?.shift?.slug || "shift_setup",
      title: "Shift",
      icon: <MdOutlineAccessTime className="text-[#0052ea] text-[32px]" />,
      completed: !!apiData?.shift?.shift_setup,
      path: "/shifts",
      isRequired: apiData?.shift?.is_required ?? true,
      canMarkComplete: apiData?.shift?.can_mark_as_complete ?? false,
    },
    {
      id: 2,
      slug: apiData?.employment?.slug || "employment_type_setup",
      title: "Employment\nTypes",
      icon: <MdOutlinePeopleAlt className="text-[#0052ea] text-[32px]" />,
      completed: !!apiData?.employment?.employment_type_setup,
      path: "/employment-management",
      isRequired: apiData?.employment?.is_required ?? true,
      canMarkComplete: apiData?.employment?.can_mark_as_complete ?? false,
    },
    {
      id: 3,
      slug: apiData?.public_holiday?.slug || "public_holiday_setup",
      title: "Public\nHolidays",
      icon: <MdOutlineEventNote className="text-[#0052ea] text-[32px]" />,
      completed: !!apiData?.public_holiday?.public_holiday_setup,
      path: "/public-holidays",
      isRequired: apiData?.public_holiday?.is_required ?? true,
      canMarkComplete: apiData?.public_holiday?.can_mark_as_complete ?? false,
    },
    {
      id: 4,
      slug: apiData?.department?.slug || "department_setup",
      title: "Department",
      icon: <MdOutlineBusiness className="text-[#0052ea] text-[32px]" />,
      completed: !!apiData?.department?.department_setup,
      path: "/department",
      isRequired: apiData?.department?.is_required ?? false,
      canMarkComplete: apiData?.department?.can_mark_as_complete ?? true,
    },
    {
      id: 5,
      slug: apiData?.leave_structure?.slug || "leave_structure_setup",
      title: "Leave\nStructure",
      icon: <MdOutlinePieChartOutline className="text-[#0052ea] text-[32px]" />,
      completed: !!apiData?.leave_structure?.leave_structure_setup,
      path: "/company-settings/leave-structure",
      isRequired: apiData?.leave_structure?.is_required ?? false,
      canMarkComplete: apiData?.leave_structure?.can_mark_as_complete ?? true,
    },
    {
      id: 6,
      slug: apiData?.salary_structure?.slug || "salary_structure_setup",
      title: "Salary\nStructure",
      icon: <FaRupeeSign className="text-[#0052ea] text-[24px]" />,
      completed: !!apiData?.salary_structure?.salary_structure_setup,
      path: "/company-settings/salary-structure",
      isRequired: apiData?.salary_structure?.is_required ?? false,
      canMarkComplete: apiData?.salary_structure?.can_mark_as_complete ?? true,
    }
  ];

  const handleStartSetup = (path) => {
    sessionStorage.setItem("onboarding_flow", "true");
    navigate(path);
  };

  const completedCount = cards.filter(c => c.completed).length;
  const progressPercent = Math.round((completedCount / cards.length) * 100);
  const isLoading = isAdmin ? list.isLoading : false;

  const value = {
    cards,
    completedCount,
    progressPercent,
    isLoading,
    isUpdatingStatus,
    confirmModalData,
    promptComplete,
    promptIncomplete,
    confirmStatusChange,
    closeConfirmationModal,
    handleStartSetup,
    companyCompletionStatus,
    isPending: !companyCompletionStatus,
    hasAccess,
    isCheckingAccess,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
